import React, { useState } from 'react';
import { useSimulationStore } from '@/store/useSimulationStore';
import { formatCurrency, formatTokens, formatRelative } from '@/utils/formatters';
import type { AgentStatus } from '@/types/agent';

/** UX mobile dédiée : navigation par onglets, activité lisible en cartes. */

const STATUS_META: Record<string, { label: string; color: string; active: boolean }> = {
  working: { label: 'EN COURS', color: '#00E676', active: true },
  thinking: { label: 'RÉFLEXION', color: '#00E5FF', active: true },
  meeting: { label: 'RÉUNION', color: '#E040FB', active: true },
  moving: { label: 'EN ROUTE', color: '#00E5FF', active: true },
  waiting_validation: { label: 'À VALIDER', color: '#FFC400', active: false },
  error: { label: 'ERREUR', color: '#FF1744', active: false },
  idle: { label: 'DISPO', color: '#6b7682', active: false },
  offline: { label: 'HORS LIGNE', color: '#3a444d', active: false },
};
const meta = (s: AgentStatus) => STATUS_META[s] ?? STATUS_META.idle;

type Tab = 'activite' | 'agents' | 'valider' | 'chiffres';

export const MobileApp: React.FC = () => {
  const [tab, setTab] = useState<Tab>('activite');
  const metrics = useSimulationStore(s => s.metrics);
  const stores = useSimulationStore(s => s.stores);
  const tasks = useSimulationStore(s => s.tasks);
  const agents = useSimulationStore(s => s.agents);

  const pending = tasks.filter(t => t.status === 'waiting_validation');
  const productsCount = stores.reduce((n, s) => n + ((s.metrics as unknown as Record<string, number>)?.products ?? 0), 0);
  const activeAgents = agents.filter(a => meta(a.status).active).length;

  return (
    <div className="flex flex-col h-full bg-bg-base text-white">
      {/* Header */}
      <header className="flex items-center justify-between px-4 py-3 border-b border-bg-border bg-bg-panel flex-shrink-0">
        <div className="flex items-center gap-2">
          <span className="font-pixel text-[10px] text-neon-cyan" style={{ textShadow: '0 0 8px #00E5FF' }}>MISSION</span>
          <span className="font-pixel text-[10px] text-neon-gold" style={{ textShadow: '0 0 8px #FFD700' }}>CTRL</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1 text-neon-green text-xs font-mono">
            <span className="w-2 h-2 rounded-full bg-neon-green mc-glow" />{activeAgents} actifs
          </span>
          {pending.length > 0 && (
            <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-neon-amber/20 text-neon-amber animate-pulse">
              {pending.length} ⚠
            </span>
          )}
        </div>
      </header>

      {/* Bandeau métriques (scroll horizontal) */}
      <div className="flex gap-2 px-3 py-2 overflow-x-auto scrollbar-thin flex-shrink-0 border-b border-bg-border bg-bg-panel/50">
        <Stat label="CA du jour" value={formatCurrency(metrics.totalRevenue)} color="#FFD700" />
        <Stat label="Marge" value={formatCurrency(metrics.totalMargin)} color="#00E676" />
        <Stat label="Commandes" value={String(metrics.totalOrders)} color="#E040FB" />
        <Stat label="Produits" value={String(productsCount)} color="#00E5FF" />
        <Stat label="Tokens" value={formatTokens(metrics.tokensUsedToday)} color="#7C4DFF" />
      </div>

      {/* Contenu */}
      <main className="flex-1 overflow-y-auto scrollbar-thin">
        {tab === 'activite' && <ActivityTab />}
        {tab === 'agents' && <AgentsTab />}
        {tab === 'valider' && <ValidateTab />}
        {tab === 'chiffres' && <StatsTab />}
      </main>

      {/* Navigation bas */}
      <nav className="grid grid-cols-4 border-t border-bg-border bg-bg-panel flex-shrink-0">
        <NavBtn icon="📡" label="Activité" active={tab === 'activite'} onClick={() => setTab('activite')} />
        <NavBtn icon="🤖" label="Agents" active={tab === 'agents'} onClick={() => setTab('agents')} />
        <NavBtn icon="✅" label="Valider" active={tab === 'valider'} onClick={() => setTab('valider')} badge={pending.length} />
        <NavBtn icon="📊" label="Chiffres" active={tab === 'chiffres'} onClick={() => setTab('chiffres')} />
      </nav>
    </div>
  );
};

const Stat: React.FC<{ label: string; value: string; color: string }> = ({ label, value, color }) => (
  <div className="flex flex-col flex-shrink-0 min-w-[88px] px-3 py-2 rounded-xl bg-bg-elevated border border-bg-border">
    <span className="font-mono text-[10px] text-gray-500 uppercase tracking-wide">{label}</span>
    <span className="font-mono text-base font-bold" style={{ color }}>{value}</span>
  </div>
);

const NavBtn: React.FC<{ icon: string; label: string; active: boolean; onClick: () => void; badge?: number }> = ({ icon, label, active, onClick, badge }) => (
  <button onClick={onClick} className={`relative flex flex-col items-center gap-0.5 py-2.5 transition-colors ${active ? 'text-neon-cyan' : 'text-gray-500'}`}>
    <span className="text-lg leading-none">{icon}</span>
    <span className="font-mono text-[10px]">{label}</span>
    {active && <span className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5 rounded-full bg-neon-cyan" />}
    {badge ? <span className="absolute top-1 right-[22%] min-w-[16px] h-4 px-1 rounded-full bg-neon-amber text-black text-[10px] font-bold flex items-center justify-center">{badge}</span> : null}
  </button>
);

// ── Onglet Activité (feed) ──────────────────────────────────────
const ActivityTab: React.FC = () => {
  const events = useSimulationStore(s => s.events);
  const agents = useSimulationStore(s => s.agents);
  if (!events.length) return <Empty emoji="📡" text="En attente d'activité…" />;
  return (
    <div className="p-3 space-y-2">
      {events.slice(0, 60).map(e => {
        const agent = agents.find(a => a.id === e.agentId);
        const color = agent?.color ?? '#6b7682';
        return (
          <div key={e.id} className="mc-fade-up flex gap-3 p-3 rounded-xl bg-bg-panel border border-bg-border">
            <span className="w-1 rounded-full flex-shrink-0" style={{ background: color }} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2">
                <span className="font-pixel text-[8px]" style={{ color }}>{agent?.codename ?? 'SYSTÈME'}</span>
                <span className="font-mono text-[10px] text-gray-600 flex-shrink-0">{formatRelative(e.timestamp)}</span>
              </div>
              <p className="font-mono text-xs text-gray-200 leading-snug mt-1 break-words">{e.message}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ── Onglet Agents ───────────────────────────────────────────────
const AgentsTab: React.FC = () => {
  const agents = useSimulationStore(s => s.agents);
  const tasks = useSimulationStore(s => s.tasks);
  const sorted = [...agents].sort((a, b) => Number(meta(b.status).active) - Number(meta(a.status).active));
  return (
    <div className="p-3 space-y-2">
      {sorted.map(a => {
        const m = meta(a.status);
        const task = tasks.find(t => t.id === a.currentTaskId);
        return (
          <div key={a.id} className="flex items-center gap-3 p-3 rounded-xl bg-bg-panel border border-bg-border">
            <div className="w-10 h-10 rounded-full flex items-center justify-center font-pixel text-[8px] flex-shrink-0"
              style={{ background: a.color + '22', border: `2px solid ${a.color}`, color: a.color, boxShadow: m.active ? `0 0 10px ${a.color}66` : 'none' }}>
              {a.codename.slice(0, 2)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-pixel text-[9px]" style={{ color: a.color }}>{a.codename}</span>
                <span className="font-mono text-[10px] text-gray-500 truncate">{a.roleName}</span>
              </div>
              <p className="font-mono text-[11px] text-gray-400 truncate mt-0.5">
                {task ? task.title : (m.active ? 'au travail…' : '—')}
              </p>
            </div>
            <span className="flex items-center gap-1 flex-shrink-0 px-2 py-1 rounded-full text-[10px] font-bold font-mono"
              style={{ color: m.color, background: m.color + '1A' }}>
              <span className={`w-1.5 h-1.5 rounded-full ${m.active ? 'mc-glow' : ''}`} style={{ background: m.color }} />
              {m.label}
            </span>
          </div>
        );
      })}
    </div>
  );
};

// ── Onglet Valider ──────────────────────────────────────────────
const ValidateTab: React.FC = () => {
  const tasks = useSimulationStore(s => s.tasks);
  const agents = useSimulationStore(s => s.agents);
  const { approveTask, rejectTask } = useSimulationStore();
  const pending = tasks.filter(t => t.status === 'waiting_validation');
  if (!pending.length) return <Empty emoji="🎉" text="Rien à valider pour l'instant" />;
  return (
    <div className="p-3 space-y-3">
      {pending.map(t => {
        const agent = agents.find(a => a.id === t.agentId);
        return (
          <div key={t.id} className="p-3 rounded-xl bg-bg-panel border border-bg-border">
            <div className="flex items-center gap-2 mb-1">
              {agent && <span className="font-pixel text-[8px]" style={{ color: agent.color }}>{agent.codename}</span>}
              <span className={`ml-auto font-mono text-[10px] px-1.5 py-0.5 rounded ${t.priority === 'critical' ? 'bg-neon-red/20 text-neon-red' : t.priority === 'high' ? 'bg-neon-orange/20 text-neon-orange' : 'bg-gray-700 text-gray-400'}`}>{t.priority.toUpperCase()}</span>
            </div>
            <p className="font-mono text-sm text-gray-100 leading-snug mb-3 break-words">{t.title}</p>
            <div className="flex gap-2">
              <button onClick={() => approveTask(t.id)} className="flex-1 py-3 rounded-lg font-mono text-sm font-bold bg-neon-green/20 text-neon-green border border-neon-green/40 active:bg-neon-green/30">✓ Approuver</button>
              <button onClick={() => rejectTask(t.id)} className="flex-1 py-3 rounded-lg font-mono text-sm font-bold bg-neon-red/10 text-neon-red border border-neon-red/30 active:bg-neon-red/20">✗ Rejeter</button>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ── Onglet Chiffres ─────────────────────────────────────────────
const StatsTab: React.FC = () => {
  const stores = useSimulationStore(s => s.stores);
  const metrics = useSimulationStore(s => s.metrics);
  const pct = Math.min(1, metrics.tokensUsedToday / (metrics.totalTokensBudget || 1));
  return (
    <div className="p-3 space-y-3">
      <div className="p-3 rounded-xl bg-bg-panel border border-bg-border">
        <div className="flex justify-between font-mono text-xs text-gray-400 mb-1"><span>Budget tokens du jour</span><span>{formatTokens(metrics.tokensUsedToday)}</span></div>
        <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
          <div className="h-full rounded-full" style={{ width: `${pct * 100}%`, background: pct > 0.85 ? 'linear-gradient(90deg,#FF6D00,#FF1744)' : 'linear-gradient(90deg,#00E5FF,#2979FF)' }} />
        </div>
      </div>
      {stores.map(s => {
        const m = (s.metrics as unknown as Record<string, number>) ?? {};
        return (
          <div key={s.id} className="p-3 rounded-xl bg-bg-panel border border-bg-border">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-lg">{s.emoji}</span>
              <span className="font-mono text-sm font-bold" style={{ color: s.color }}>{s.name}</span>
            </div>
            <div className="grid grid-cols-3 gap-2 text-center">
              <Mini label="Produits" value={String(m.products ?? 0)} />
              <Mini label="Commandes" value={String(m.orders ?? 0)} />
              <Mini label="CA" value={formatCurrency(m.revenue ?? 0)} />
            </div>
          </div>
        );
      })}
    </div>
  );
};

const Mini: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="py-1.5 rounded-lg bg-bg-elevated">
    <div className="font-mono text-sm font-bold text-gray-100">{value}</div>
    <div className="font-mono text-[10px] text-gray-500">{label}</div>
  </div>
);

const Empty: React.FC<{ emoji: string; text: string }> = ({ emoji, text }) => (
  <div className="flex flex-col items-center justify-center h-full gap-2 text-gray-500 px-6 text-center">
    <span className="text-4xl">{emoji}</span>
    <span className="font-mono text-sm">{text}</span>
  </div>
);
